import { useCallback, useMemo, useState } from "react";
import LargePopupModalHeader from "../../../Containers/LargePopupModalHeader";
import RectangleButton from "../../../Buttons/RectangleButton";
import { Colors } from "../../../../utils/colors";
import { MassTextsActionItem } from "../../MassTexts/Items/MassTextsActionItem";
import { Icon } from "@iconify/react";
import { EventType, MassTextAttachmentId } from "@markit/common.types";
import SelectProductItemModal from "../../Products/SelectProductItemModal";
import { useSelector } from "react-redux";
import { getEventState } from "../../../../redux/slices/eventSlice";
import ConfirmDeleteModal from "../../../Containers/ConfirmPopups/ConfirmDeleteModal";
import { getProductDetails } from "../../../../utils/productUtils";
import CustomTextField from "../../../CustomTextField";
import { censoredWordsDetected, detectedCensored } from "@markit/common.utils";
import SelectableOptionCard from "../../../Containers/Items/SelectableOptionCard";

enum LinkDisplayType {
  INLINE = "inline",
  BUTTON = "button",
}

type DMAutomationAttachmentsPanelProps = {
  closeOnPress: () => void;
  defaultListView: number;
  defaultAttachmentId: string | undefined;
  finalizeAttachment: (
    attachmentItemId: string,
    buttonTitle: string,
    isInline: boolean
  ) => void;
};

const DMAutomationAttachmentsPanel = (
  props: DMAutomationAttachmentsPanelProps
) => {
  const {
    closeOnPress,
    defaultListView,
    defaultAttachmentId,
    finalizeAttachment,
  } = props;
  const { events } = useSelector(getEventState).events;
  const [itemId, setItemId] = useState<string | undefined>(defaultAttachmentId);
  const [selectedEventType, setSelectedEventType] = useState<
    EventType | undefined
  >(undefined);
  const [listView, setListView] = useState(defaultListView || 0);
  const [showConfirmBackModal, setShowConfirmBackModal] = useState(false);
  const [showDisplayStep, setShowDisplayStep] = useState(false);
  const [linkDisplayType, setLinkDisplayType] = useState<
    LinkDisplayType | undefined
  >(LinkDisplayType.BUTTON);
  const [buttonTitle, setButtonTitle] = useState("");
  const [buttonTitleError, setButtonTitleError] = useState("");

  const styles = {
    container: {
      borderRadius: 12,
      border: `0.5px solid ${Colors.GRAY2}`,
      backgroundColor: Colors.WHITE1,
      padding: 14,
      gap: 10,
    },
  };

  const userEvents = useMemo(() => {
    return events.filter((event) => !event.isDraft);
  }, [events]);

  const madeChanges = useMemo(() => !!itemId, [itemId]);

  const createEnabled = useMemo(
    () => madeChanges && linkDisplayType !== undefined,
    [madeChanges, linkDisplayType]
  );

  const confirmBackOnPress = useCallback(() => {
    if (selectedEventType) {
      setSelectedEventType(undefined);
    } else {
      closeOnPress();
    }
  }, [selectedEventType, setSelectedEventType, closeOnPress]);

  const backOnPress = useCallback(() => {
    if (showDisplayStep) {
      setShowDisplayStep(false);
    } else if (madeChanges && !selectedEventType) {
      setShowConfirmBackModal(true);
    } else {
      confirmBackOnPress();
    }
  }, [madeChanges, selectedEventType, confirmBackOnPress, showDisplayStep]);

  const attachOnPress = useCallback(() => {
    if (!itemId) return;
    if (linkDisplayType === LinkDisplayType.BUTTON) {
      if (!buttonTitle.length) {
        setButtonTitleError("Button title cannot be empty.");
        return;
      }
      if (detectedCensored(buttonTitle)) {
        setButtonTitleError(
          "Flagged words detected: " + censoredWordsDetected(buttonTitle)
        );
        return;
      }
    }
    finalizeAttachment(
      itemId,
      buttonTitle,
      linkDisplayType === LinkDisplayType.INLINE
    );
  }, [finalizeAttachment, itemId, buttonTitle, linkDisplayType]);

  const nextOnPress = useCallback(() => {
    setShowDisplayStep(true);
  }, []);

  const renderActionItem = useCallback(
    (
      title: string,
      subtext: string,
      iconName: string,
      attachmentId: MassTextAttachmentId,
      disabled: boolean
    ) => (
      <MassTextsActionItem
        title={title}
        subtext={subtext}
        icon={
          <Icon icon={iconName} height={28} style={{ color: Colors.BLACK }} />
        }
        onPress={() => setItemId(itemId === attachmentId ? "" : attachmentId)}
        isCheckSelected={itemId === attachmentId}
        checkColor={Colors.BLACK}
        disabled={disabled}
      />
    ),
    [itemId]
  );

  return (
    <>
      <SelectProductItemModal
        headerComp={
          <LargePopupModalHeader
            title={
              selectedEventType && !showDisplayStep
                ? getProductDetails(selectedEventType).title
                : "Add Link"
            }
            altHeaderLeft={
              selectedEventType || showDisplayStep
                ? "ion:chevron-back"
                : undefined
            }
            backOnPress={backOnPress}
            headerRight={
              <RectangleButton
                buttonLabel={showDisplayStep ? "Create" : "Next"}
                onPress={showDisplayStep ? attachOnPress : nextOnPress}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                disabled={showDisplayStep ? !createEnabled : !madeChanges}
                containerStyles={{
                  borderRadius: 100,
                  padding: "10px 14px",
                  height: 36,
                }}
              />
            }
          />
        }
        extraStepComp={
          showDisplayStep ? (
            <div className="ColumnNormal" style={styles.container}>
              <div className="ColumnNormal" style={{ gap: 4 }}>
                <span className="bodyMedium">Display your link</span>
                <span className="smallBodySubtext">
                  Decide how you want to attach the link to the DM
                </span>
              </div>
              <SelectableOptionCard
                title="Inline Link"
                subtext="Shows up directly within the message as a url with a preview of the link."
                isSelected={linkDisplayType === LinkDisplayType.INLINE}
                onSelect={() => setLinkDisplayType(LinkDisplayType.INLINE)}
              />
              <SelectableOptionCard
                title="Button Link"
                subtext="Shows up as a clickable button that opens the link directly."
                isSelected={linkDisplayType === LinkDisplayType.BUTTON}
                onSelect={() => setLinkDisplayType(LinkDisplayType.BUTTON)}
              >
                <div className="ColumnNormal" style={{ gap: 7 }}>
                  <div className="AlignedRowSpaced">
                    <span className="bodyRegular">Button Title</span>
                    <span className="smallBodySubtext">
                      {buttonTitle.length}/20
                    </span>
                  </div>
                  <CustomTextField
                    value={buttonTitle}
                    placeholder="Enter title here..."
                    inputMode="text"
                    borderRadius={12}
                    onChange={(e: any) => {
                      setButtonTitleError("");
                      setButtonTitle(e.target.value);
                    }}
                    maxLength={20}
                    error={buttonTitleError !== ""}
                    containerStyles={{ width: undefined, alignSelf: undefined }}
                  />
                  {buttonTitleError ? (
                    <span
                      className="smallBodySubtext"
                      style={{ color: Colors.RED3 }}
                    >
                      {buttonTitleError}
                    </span>
                  ) : null}
                </div>
              </SelectableOptionCard>
            </div>
          ) : undefined
        }
        generalTabComp={
          <div className="ColumnNormal">
            {renderActionItem(
              "Link to Subscribe",
              "A link to your profile subscribe page",
              "ion:person-circle",
              MassTextAttachmentId.SUBSCRIBE_LINK,
              false
            )}
          </div>
        }
        items={itemId ? [itemId] : []}
        setItems={(newItem: string) => {
          if (itemId === newItem) {
            setItemId(undefined);
          } else {
            setItemId(newItem);
          }
        }}
        selectedEventType={selectedEventType}
        setSelectedEventType={setSelectedEventType}
        userEvents={userEvents}
        listView={listView}
        setListView={setListView}
        showHiddenLabel={false}
      />
      <ConfirmDeleteModal
        heading="If you go back, your changes will be discarded."
        subtext="Continue?"
        deleteButtonText="Discard Changes"
        hideModal={!showConfirmBackModal}
        setIsVisible={setShowConfirmBackModal}
        deleteOnPress={confirmBackOnPress}
      />
    </>
  );
};

export default DMAutomationAttachmentsPanel;
